import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'

import designSystemEn from '#design-system/locales/en.json'
import enTranslation from '~/locales/en/translation.json'

/**
 * ARP i18n setup. One default locale (`en`); resources are imported statically so init is sync —
 * no Suspense gymnastics, no runtime fetch.
 *
 * Two namespaces: `translation` holds this app's copy, `design_system` holds the design system's.
 * It lives under `design-system/locales/` because it ships beyond this app — web and guest frames
 * each register the same bundle with whatever i18n instance they already run.
 * A namespace rather than a key inside `translation` keeps it a self-contained unit those
 * consumers can load and type on its own, with no chance of colliding with their copy.
 *
 * The `CustomTypeOptions` augmentation below makes `useTranslation()` from `react-i18next` fully
 * key-typed against the JSON shape. No need for a custom hook or codegen plugin: TypeScript
 * infers the dotted-key constraint from `typeof resources['en']` directly.
 *
 * TODO: When a second language is needed, switch this to per-language dynamic `import()` so each
 * locale can be code-split, and add `i18next-browser-languagedetector`.
 */
export const resources = {
  en: { translation: enTranslation, design_system: designSystemEn },
} as const

void i18next.use(initReactI18next).init({
  resources,
  lng: 'en',
  fallbackLng: 'en',
  defaultNS: 'translation',
  interpolation: { escapeValue: false },
})

declare module 'i18next' {
  interface CustomTypeOptions {
    defaultNS: 'translation'
    resources: (typeof resources)['en']
    returnNull: false
    returnEmptyString: false
  }
}
